import React from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {MultiSelect} from "primereact/multiselect";
import RepeatableListField from "./repeatable-list-field";
import {codexModes, priorities, productAreas, requestTypes, steps} from "../utils/product-brief-schema";

export default function ProductBriefForm({brief, setBrief, activeStep, setActiveStep, errors, translate}) {
    const update = (key, value) => setBrief((current) => ({...current, [key]: value}));
    const updateTechnical = (key, value) => setBrief((current) => ({...current, technicalNotes: {...current.technicalNotes, [key]: value}}));

    const dropdownOptions = (items) => items.map((item) => ({...item, label: translate(item.labelKey)}));
    const areaOptions = productAreas.map((item) => ({label: item, value: item}));

    return (
        <section className="pbb-form-panel">
            <div className="pbb-panel-header">
                <div>
                    <h2>{translate(steps[activeStep].titleKey)}</h2>
                    <p>{translate("pbb-step-description")}</p>
                </div>
                <span>{activeStep + 1} / {steps.length}</span>
            </div>

            {activeStep === 0 && (
                <div className="pbb-form-grid">
                    <Field label={translate("pbb-request-type")} error={errors.requestType && translate(errors.requestType)}>
                        <Dropdown value={brief.requestType} options={dropdownOptions(requestTypes)} onChange={(event) => update("requestType", event.value)} placeholder={translate("pbb-select-placeholder")}/>
                    </Field>
                    <Field label={translate("pbb-feature-name")} error={errors.featureName && translate(errors.featureName)}>
                        <InputText value={brief.featureName} onChange={(event) => update("featureName", event.target.value)} placeholder={translate("pbb-feature-name-placeholder")}/>
                    </Field>
                    <Field label={translate("pbb-priority")}>
                        <Dropdown value={brief.priority} options={dropdownOptions(priorities)} onChange={(event) => update("priority", event.value)}/>
                    </Field>
                    <Field label={translate("pbb-involved-areas")}>
                        <InputText value={brief.involvedAreas} onChange={(event) => update("involvedAreas", event.target.value)} placeholder={translate("pbb-involved-areas-placeholder")}/>
                    </Field>
                    <Field label={translate("pbb-requester")}>
                        <InputText value={brief.requester} onChange={(event) => update("requester", event.target.value)} placeholder={translate("pbb-requester-placeholder")}/>
                    </Field>
                    <Field label={translate("pbb-request-date")}>
                        <InputText type="date" value={brief.requestDate} onChange={(event) => update("requestDate", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-codex-mode")} wide>
                        <Dropdown value={brief.codexMode} options={dropdownOptions(codexModes)} onChange={(event) => update("codexMode", event.value)}/>
                    </Field>
                </div>
            )}

            {activeStep === 1 && (
                <div className="pbb-form-grid">
                    <p className="pbb-guidance">{translate("pbb-problem-guidance")}</p>
                    <Field label={translate("pbb-problem")} error={errors.problem && translate(errors.problem)} wide>
                        <InputTextarea autoResize rows={5} value={brief.problem} onChange={(event) => update("problem", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-target-user")} error={errors.targetUser && translate(errors.targetUser)}>
                        <InputText value={brief.targetUser} onChange={(event) => update("targetUser", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-target-role")}>
                        <InputText value={brief.targetRole} onChange={(event) => update("targetRole", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-business-impact")} wide>
                        <InputTextarea autoResize rows={3} value={brief.businessImpact} onChange={(event) => update("businessImpact", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-existing-product-relation")} wide>
                        <InputTextarea autoResize rows={3} value={brief.existingProductRelation} onChange={(event) => update("existingProductRelation", event.target.value)}/>
                    </Field>
                </div>
            )}

            {activeStep === 2 && (
                <div className="pbb-form-grid">
                    <div className="pbb-wide">
                        <RepeatableListField label={translate("pbb-user-scenario")} values={brief.userScenario} onChange={(value) => update("userScenario", value)} placeholder={translate("pbb-scenario-placeholder")} error={errors.userScenario} translate={translate}/>
                    </div>
                    <RoleFields title={translate("pbb-allowed-roles")} values={brief.allowedRoles} keys={["role", "action", "description"]} error={errors.allowedRoles} onChange={(value) => update("allowedRoles", value)} translate={translate}/>
                    <RoleFields title={translate("pbb-denied-roles")} values={brief.deniedRoles} keys={["role", "limitation", "description"]} onChange={(value) => update("deniedRoles", value)} translate={translate}/>
                    <Field label={translate("pbb-product-location")} wide>
                        <InputTextarea autoResize rows={3} value={brief.productLocation} onChange={(event) => update("productLocation", event.target.value)}/>
                    </Field>
                </div>
            )}

            {activeStep === 3 && (
                <div className="pbb-form-grid">
                    <Field label={translate("pbb-expected-outcome")} error={errors.expectedOutcome && translate(errors.expectedOutcome)} wide>
                        <InputTextarea autoResize rows={4} value={brief.expectedOutcome} onChange={(event) => update("expectedOutcome", event.target.value)}/>
                    </Field>
                    <div className="pbb-wide">
                        <RepeatableListField label={translate("pbb-acceptance-criteria")} values={brief.acceptanceCriteria} onChange={(value) => update("acceptanceCriteria", value)} placeholder={translate("pbb-acceptance-placeholder")} error={errors.acceptanceCriteria} translate={translate}/>
                    </div>
                    <Field label={translate("pbb-connected-areas")} wide>
                        <MultiSelect value={brief.connectedAreas} options={areaOptions} onChange={(event) => update("connectedAreas", event.value)} display="chip" placeholder={translate("pbb-select-placeholder")}/>
                    </Field>
                    <p className="pbb-guidance pbb-wide">{translate("pbb-connected-areas-note")}</p>
                </div>
            )}

            {activeStep === 4 && (
                <div className="pbb-form-grid">
                    <div className="pbb-wide">
                        <RepeatableListField label={translate("pbb-out-of-scope")} values={brief.outOfScope} onChange={(value) => update("outOfScope", value)} placeholder={translate("pbb-out-of-scope-placeholder")} translate={translate}/>
                    </div>
                    <div className="pbb-wide">
                        <RepeatableListField label={translate("pbb-open-questions")} values={brief.openQuestions} onChange={(value) => update("openQuestions", value)} placeholder={translate("pbb-open-question-placeholder")} translate={translate}/>
                    </div>
                    <Field label={translate("pbb-dependencies")} wide>
                        <InputTextarea autoResize rows={3} value={brief.dependencies} onChange={(event) => update("dependencies", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-backend-receipt-path")}>
                        <InputText value={brief.technicalNotes.backendReceiptPath} onChange={(event) => updateTechnical("backendReceiptPath", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-bcr-path")}>
                        <InputText value={brief.technicalNotes.bcrPath} onChange={(event) => updateTechnical("bcrPath", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-known-contract")} wide>
                        <InputTextarea autoResize rows={2} value={brief.technicalNotes.knownEndpointOrContract} onChange={(event) => updateTechnical("knownEndpointOrContract", event.target.value)}/>
                    </Field>
                    <Field label={translate("pbb-extra-technical-notes")} wide>
                        <InputTextarea autoResize rows={3} value={brief.technicalNotes.extraNotes} onChange={(event) => updateTechnical("extraNotes", event.target.value)}/>
                    </Field>
                </div>
            )}

            {activeStep === 5 && (
                <div className="pbb-review-box">
                    <h3>{translate("pbb-review-ready-title")}</h3>
                    <p>{translate("pbb-review-ready-description")}</p>
                    {Object.keys(errors).length ? (
                        <ul>
                            {Object.entries(errors).map(([key, value]) => <li key={key}>{translate(value)}</li>)}
                        </ul>
                    ) : (
                        <div className="pbb-valid-state">
                            <Checkbox checked readOnly/>
                            <span>{translate("pbb-form-valid")}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="pbb-step-actions">
                <Button type="button" icon="pi pi-angle-right" label={translate("previous")} outlined disabled={activeStep === 0} onClick={() => setActiveStep(Math.max(activeStep - 1, 0))}/>
                <Button type="button" icon="pi pi-angle-left" iconPos="right" label={translate("next")} disabled={activeStep === steps.length - 1} onClick={() => setActiveStep(Math.min(activeStep + 1, steps.length - 1))}/>
            </div>
        </section>
    );
}

function Field({label, error, wide, children}) {
    return (
        <div className={`pbb-field ${wide ? "pbb-wide" : ""}`}>
            <label>{label}</label>
            {children}
            {error && <small className="p-error">{error}</small>}
        </div>
    );
}

function RoleFields({title, values, keys, onChange, error, translate}) {
    const items = values?.length ? values : [keys.reduce((acc, key) => ({...acc, [key]: ""}), {})];

    const update = (index, key, value) => {
        onChange(items.map((item, itemIndex) => itemIndex === index ? {...item, [key]: value} : item));
    };

    const add = () => onChange([...items, keys.reduce((acc, key) => ({...acc, [key]: ""}), {})]);
    const remove = (index) => onChange(items.filter((_, itemIndex) => itemIndex !== index));

    return (
        <div className="pbb-field-group pbb-wide">
            <label>{title}</label>
            {items.map((item, index) => (
                <div className="pbb-role-row" key={index}>
                    {keys.map((key) => (
                        <InputText key={key} value={item[key]} onChange={(event) => update(index, key, event.target.value)} placeholder={translate(`pbb-role-${key}`)}/>
                    ))}
                    <Button type="button" icon="pi pi-trash" text rounded severity="danger" disabled={items.length === 1} onClick={() => remove(index)}/>
                </div>
            ))}
            {error && <small className="p-error">{translate(error)}</small>}
            <Button type="button" icon="pi pi-plus" label={translate("pbb-add-role")} outlined className="w-fit" onClick={add}/>
        </div>
    );
}
